import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, FolderOpen } from "lucide-react";
import api from "@/lib/api";
import { SectionTitle } from "@/components/Section";
import SubjectIcon from "@/components/SubjectIcon";
import KitCard from "@/components/KitCard";
import AuthGateModal from "@/components/AuthGateModal";
import { useKitAccess } from "@/lib/useKitAccess";

export default function RevisionKits() {
  const [params, setParams] = useSearchParams();
  const subject = params.get("subject") || "all";
  const [subjects, setSubjects] = useState([]);
  const [kits, setKits] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);
  const [pendingNext, setPendingNext] = useState(null);
  const openKit = useKitAccess(setGateOpen, setPendingNext);

  useEffect(() => {
    api.get("/subjects").then(({ data }) => setSubjects(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const url = subject === "all" ? "/kits?limit=200" : `/kits?subject=${subject}&limit=200`;
    api.get(url).then(({ data }) => setKits(data)).catch(() => {}).finally(() => setLoading(false));
  }, [subject]);

  const setSubject = (slug) => {
    if (slug === "all") setParams({});
    else setParams({ subject: slug });
  };

  const filtered = kits.filter((k) => k.title.toLowerCase().includes(q.toLowerCase()) || (k.description || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <SectionTitle sub="All kits are free — create a free account to view and download." testId="kits-page-title">
        Revision Kits Library
      </SectionTitle>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search kits…"
            className="w-full rounded-sm border border-[#D1D5DB] bg-white py-2 pl-9 pr-3 text-sm focus:border-[#1F3A68]"
            data-testid="kit-search"
          />
        </div>
        <span className="text-sm text-gray-500">{filtered.length} kit{filtered.length === 1 ? "" : "s"}</span>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Subject sidebar */}
        <aside className="lg:w-60 lg:shrink-0">
          <div className="gov-card overflow-hidden">
            <div className="gov-card-header px-4 py-2.5 font-serif text-sm font-bold">Filter by Subject</div>
            <ul className="divide-y divide-[#E5E7EB]">
              <li>
                <button onClick={() => setSubject("all")} className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm ${subject === "all" ? "bg-[#1F3A68]/10 font-semibold text-[#0B2545]" : "text-gray-700 hover:bg-gray-50"}`} data-testid="filter-all">
                  <FolderOpen size={18} className="text-[#1F3A68]" /> All Subjects
                </button>
              </li>
              {subjects.map((s) => (
                <li key={s.id}>
                  <button onClick={() => setSubject(s.slug)} className={`flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm ${subject === s.slug ? "bg-[#1F3A68]/10 font-semibold text-[#0B2545]" : "text-gray-700 hover:bg-gray-50"}`} data-testid={`filter-${s.slug}`}>
                    <span className="flex items-center gap-2"><SubjectIcon name={s.icon} size={18} /> {s.name}</span>
                    <span className="text-xs text-gray-400">{s.kit_count}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Kits grid */}
        <div className="flex-1">
          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {[...Array(6)].map((_, i) => <div key={i} className="h-40 animate-pulse rounded-sm bg-gray-200" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="gov-card flex flex-col items-center justify-center py-16 text-center" data-testid="kits-empty">
              <FolderOpen size={40} className="text-gray-300" />
              <p className="mt-3 font-serif text-lg font-bold text-[#0B2545]">No kits found</p>
              <p className="text-sm text-gray-500">New kits are added every week — check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="kits-grid">
              {filtered.map((k) => <KitCard key={k.id} kit={k} onOpen={openKit} />)}
            </div>
          )}
        </div>
      </div>

      <AuthGateModal open={gateOpen} onOpenChange={setGateOpen} nextPath={pendingNext} />
    </div>
  );
}
